import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Trash2, ShieldBan, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DataTable } from '@/components/common/DataTable';
import { formatDate } from '@/utils';
import { useGetCouponsQuery, useUpdateCouponStatusMutation } from '@/store/api/couponApi';
import { CouponStatus } from '@/types';
import { useDebounce } from '@/hooks';

/* ===================== TYPES ===================== */

export interface Coupon {
  id: number;
  code: string;
  description: string;
  discountType: 'Percentage' | 'Fixed' | 'FractionBonus';
  discountValue: string;
  bonusFractions: number | null;
  maxDiscountAmount: string;
  minPurchaseAmount: string;
  totalUsageLimit: number;
  perUserLimit: number;
  validFrom: string;
  validTill: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  status: CouponStatus;
}

/* ===================== STATUS BADGE ===================== */

function CouponStatusBadge({ status }: { status: CouponStatus }) {
  const config: any = {
    Active: {
      label: 'Active',
      container: 'bg-green-100 text-green-800',
      dot: 'bg-green-500',
    },
    Blocked: {
      label: 'Disabled',
      container: 'bg-red-100 text-red-800',
      dot: 'bg-red-500',
    },
  };

  const { label, container, dot } = config[status];

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${container}`}>
      <span className={`mr-1 h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/* ===================== ACTIONS ===================== */

function CouponActions({ coupon }: { coupon: Coupon }) {
  const [updateCouponStatus] = useUpdateCouponStatusMutation();

  const handleStatusChange = async (data: { isActive?: boolean; isDeleted?: boolean }) => {
    await updateCouponStatus({
      couponId: coupon.id,
      ...data,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {coupon.status !== 'Active' && (
          <DropdownMenuItem onClick={() => handleStatusChange({ isActive: true })}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Activate
          </DropdownMenuItem>
        )}

        {coupon.status === 'Active' && (
          <DropdownMenuItem onClick={() => handleStatusChange({ isActive: false })}>
            <ShieldBan className="mr-2 h-4 w-4" />
            Disable
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive"
          onClick={() => handleStatusChange({ isDeleted: true })}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ===================== PAGE ===================== */

export function CouponsPage() {
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isError } = useGetCouponsQuery({
    skip: pageIndex * pageSize,
    take: pageSize,
    search: debouncedSearch,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0); // 👈 always reset to first page
  };

  const coupons: Coupon[] =
    data?.data?.map((c: any) => {
      let status: CouponStatus = 'Active';
      if (!c.isActive) status = 'Blocked';
      return {
        ...c,
        status,
      };
    }) ?? [];

  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<Coupon>[] = [
    {
      header: 'Code',
      cell: ({ row }) => <span className="font-mono font-semibold">{row.original.code}</span>,
    },
    {
      header: 'Discount',
      cell: ({ row }) => {
        const c = row.original;
        return c.discountType === 'Percentage' ? `${c.discountValue}%` : `₹${c.discountValue}`;
      },
    },
    {
      header: 'Total Usage Limit',
      cell: ({ row }) => <span>{row.original.totalUsageLimit}</span>,
    },
    {
      header: 'Per User Limit',
      cell: ({ row }) => <span>{row.original.perUserLimit}</span>,
    },
    {
      header: 'Validity',
      cell: ({ row }) => (
        <div className="text-sm">
          <div>{formatDate(new Date(row.original.validFrom))}</div>
          <div className="text-muted-foreground">
            to {formatDate(new Date(row.original.validTill))}
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) => <CouponStatusBadge status={row.original.status} />,
    },
    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <CouponActions coupon={row.original} />,
      enableSorting: false,
    },
  ];

  if (isLoading) return <div className="p-6">Loading coupons...</div>;
  if (isError) return <div className="p-6 text-red-500">Failed to load coupons</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Coupons</h2>
        <Button onClick={() => navigate('/create-coupon')}>Create Coupon</Button>
      </div>

      <DataTable
        data={coupons}
        columns={columns}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
